import { useEffect, useRef, useState } from "react";
import { Mic, Square, Zap, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useDictation, LIVE_SUPPORTED } from "@/lib/useDictation";
import { parseUtterance, joinText } from "@/lib/dictation";
import { loadLexique, applyLexique } from "@/lib/lexique";
import { useAuth } from "@/context/AuthContext";
import { CREATE_CLINICAL, REPORT_WRITE } from "@/lib/rbac";

const L = {
  fr: { dict: "Dicter", stop: "Arrêter", live: "Direct", precise: "Précis IA", on: "Dictée en cours — corrigez le texte à tout moment", listen: "J'écoute…", micro: "Micro refusé : autorisez le microphone dans le navigateur", help: "« point », « virgule », « à la ligne », « effacer »", nolive: "Le mode direct demande Chrome ou Edge" },
  en: { dict: "Dictate", stop: "Stop", live: "Live", precise: "AI precise", on: "Dictating — edit the text at any time", listen: "Listening…", micro: "Microphone blocked: allow it in your browser", help: "“period”, “comma”, “new line”, “delete that”", nolive: "Live mode requires Chrome or Edge" },
  de: { dict: "Diktieren", stop: "Stopp", live: "Live", precise: "KI präzise", on: "Diktat läuft — Text jederzeit korrigierbar", listen: "Ich höre zu…", micro: "Mikrofon blockiert: im Browser erlauben", help: "„Punkt“, „Komma“, „neue Zeile“, „löschen“", nolive: "Live-Modus erfordert Chrome oder Edge" },
  ar: { dict: "إملاء", stop: "إيقاف", live: "مباشر", precise: "دقيق بالذكاء", on: "الإملاء جارٍ — يمكنك تصحيح النص في أي وقت", listen: "أستمع…", micro: "الميكروفون محظور: اسمح به في المتصفح", help: "«نقطة»، «فاصلة»، «سطر جديد»، «احذف»", nolive: "الوضع المباشر يتطلب Chrome أو Edge" },
};

function ModeToggle({ mode, setMode, t, testId }) {
  const btn = (m, Icon, label, dis) => (
    <button type="button" disabled={dis} onClick={() => setMode(m)} title={dis ? t.nolive : undefined} data-testid={`${testId}-mode-${m}`}
            className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors disabled:opacity-40 ${mode === m ? "bg-white shadow text-sky-700" : "text-slate-500 hover:text-slate-700"}`}>
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
  return (
    <div className="flex items-center bg-slate-100 rounded-lg p-0.5 text-[11px] font-semibold">
      {btn("live", Zap, t.live, !LIVE_SUPPORTED)}
      {btn("precise", Sparkles, t.precise, false)}
    </div>
  );
}

export const DictationButton = ({ value, onChange, disabled, testId = "dictation" }) => {
  const { lang } = useLang();
  const t = L[lang] || L.fr;
  const valRef = useRef(value || "");
  valRef.current = value || "";
  const segs = useRef([]);
  const lex = useRef([]);
  const { user } = useAuth();
  const canLex = [...CREATE_CLINICAL, ...REPORT_WRITE].includes(user?.role);
  useEffect(() => { if (canLex) loadLexique(user?.id).then((e) => { lex.current = e; }); }, [canLex, user?.id]);
  const [mode, setModeState] = useState(() => {
    const m = localStorage.getItem("dictee_mode");
    return m === "precise" || !LIVE_SUPPORTED ? "precise" : "live";
  });
  const onFinal = (raw) => {
    const r = parseUtterance(applyLexique(raw, lex.current), lang);
    const cur = valRef.current;
    if (r.erase) {
      const last = segs.current.pop();
      if (last && cur.endsWith(last.added)) onChange(cur.slice(0, cur.length - last.added.length));
      return;
    }
    const next = joinText(cur, r.text);
    segs.current.push({ added: next.slice(cur.length) });
    valRef.current = next;
    onChange(next);
  };
  const d = useDictation({ lang, mode, onFinal });
  const setMode = (m) => { localStorage.setItem("dictee_mode", m); window.dispatchEvent(new CustomEvent("dictee-mode", { detail: m })); };
  const { active, stop } = d;
  useEffect(() => {
    const h = (e) => { if (active) stop(); setModeState(e.detail); };
    window.addEventListener("dictee-mode", h);
    return () => window.removeEventListener("dictee-mode", h);
  }, [active, stop]);
  return (
    <div className="mt-1.5" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" disabled={disabled} onClick={d.active ? d.stop : d.start} data-testid={`${testId}-toggle`}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-40 ${d.active ? "bg-red-600 text-white hover:bg-red-700" : "bg-sky-50 text-sky-700 ring-1 ring-sky-200 hover:bg-sky-100"}`}>
          {d.active ? <Square className="h-3 w-3 fill-current" /> : <Mic className="h-3.5 w-3.5" />} {d.active ? t.stop : t.dict}
        </button>
        {!disabled && <ModeToggle mode={mode} setMode={setMode} t={t} testId={testId} />}
        {!d.active && !disabled && <span className="text-[10px] text-slate-400 hidden md:inline">{t.help}</span>}
      </div>
      {d.active && (
        <div className="mt-1.5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50/60 px-2.5 py-1.5 text-xs" data-testid={`${testId}-live`}>
          <span className="relative mt-1 flex h-2 w-2 shrink-0"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" /></span>
          <div className="min-w-0">
            <div className="font-semibold text-red-700">{t.on}</div>
            <div className="text-slate-600 italic truncate" data-testid={`${testId}-interim`}>{d.interim || t.listen}</div>
          </div>
        </div>
      )}
      {d.error && <div className="mt-1 text-[11px] text-red-600" data-testid={`${testId}-error`}>{d.error === "micro" ? t.micro : d.error}</div>}
    </div>
  );
};
